import React, { useEffect, useState } from "react";
import axios from "axios";
import { useAuth } from "@/auth/AuthContext";
import { useI18n } from "@/i18n/I18nContext";
import FolioRule from "@/components/FolioRule";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const empty = {
  title_en: "",
  title_es: "",
  excerpt_en: "",
  excerpt_es: "",
  body_en: "",
  body_es: "",
  category: "",
  cover_image: "",
  published: true,
};

export default function AdminArchive() {
  const { token } = useAuth();
  const { t } = useI18n();
  const headers = { Authorization: `Bearer ${token}` };
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null); // null | "new" | id
  const [form, setForm] = useState(empty);

  const load = () => {
    setLoading(true);
    axios
      .get(`${API}/admin/archive`, { headers })
      .then((r) => setRows(r.data))
      .catch(() => setRows([]))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  const startNew = () => {
    setEditing("new");
    setForm(empty);
  };
  const startEdit = (r) => {
    setEditing(r.id);
    setForm({
      title_en: r.title_en,
      title_es: r.title_es,
      excerpt_en: r.excerpt_en,
      excerpt_es: r.excerpt_es,
      body_en: r.body_en,
      body_es: r.body_es,
      category: r.category,
      cover_image: r.cover_image || "",
      published: r.published,
    });
  };

  const save = async (e) => {
    e.preventDefault();
    try {
      if (editing === "new") {
        await axios.post(`${API}/admin/archive`, form, { headers });
        toast.success("Created");
      } else {
        await axios.put(`${API}/admin/archive/${editing}`, form, { headers });
        toast.success("Updated");
      }
      setEditing(null);
      load();
    } catch (_e) {
      toast.error("Failed");
    }
  };

  const remove = async (id) => {
    if (!window.confirm(t("admin.confirm_delete"))) return;
    try {
      await axios.delete(`${API}/admin/archive/${id}`, { headers });
      toast.success("Deleted");
      load();
    } catch (_e) {
      toast.error("Failed");
    }
  };

  return (
    <div data-testid="admin-archive-page">
      <FolioRule label="02 / ARCHIVE" className="mb-12" />

      <div className="flex items-end justify-between gap-4 flex-wrap">
        <h1 className="font-display text-5xl md:text-6xl leading-tight text-cream">{t("admin.archive")}</h1>
        <button onClick={startNew} className="btn-luxe-solid" data-testid="archive-new">
          {t("admin.new_entry")}
          <span className="arrow-tick" />
        </button>
      </div>

      <div className="mt-14 border border-gold/20">
        {loading ? (
          <div className="px-6 py-10 font-mono text-[10px] tracking-widest2 uppercase text-cream/55">
            Loading…
          </div>
        ) : rows.length === 0 ? (
          <div className="px-6 py-10 font-mono text-[10px] tracking-widest2 uppercase text-cream/45">
            Empty.
          </div>
        ) : (
          rows.map((r, i) => (
            <div
              key={r.id}
              className={`grid md:grid-cols-12 gap-4 px-6 py-5 items-center ${
                i > 0 ? "border-t border-gold/15" : ""
              }`}
              data-testid={`archive-row-${r.id}`}
            >
              <div className="md:col-span-5 font-display italic text-cream text-xl">{r.title_en}</div>
              <div className="md:col-span-3 font-mono text-[10px] tracking-widest2 uppercase text-gold/80">
                {r.category}
              </div>
              <div className="md:col-span-2 font-mono text-[10px] tracking-widest2 uppercase text-cream/55">
                {r.published ? "Published" : "Draft"}
              </div>
              <div className="md:col-span-2 flex gap-3 justify-end">
                <button
                  onClick={() => startEdit(r)}
                  className="font-mono text-[10px] tracking-widest2 uppercase text-cream/70 hover:text-gold transition-colors"
                  data-testid={`edit-${r.id}`}
                >
                  {t("admin.edit")}
                </button>
                <button
                  onClick={() => remove(r.id)}
                  className="font-mono text-[10px] tracking-widest2 uppercase text-cream/70 hover:text-destructive transition-colors"
                  data-testid={`delete-archive-${r.id}`}
                >
                  {t("admin.delete")}
                </button>
              </div>
            </div>
          ))
        )}
      </div>

      {editing !== null ? (
        <div className="fixed inset-0 z-50 bg-ink/80 backdrop-blur-sm flex items-start md:items-center justify-center p-4 overflow-y-auto" data-testid="archive-editor">
          <form
            onSubmit={save}
            className="bg-ink-grain border border-gold/40 w-full max-w-3xl p-6 md:p-10 my-12"
          >
            <div className="flex items-center justify-between mb-8">
              <h2 className="font-display italic text-3xl md:text-4xl text-cream">
                {editing === "new" ? t("admin.new_entry") : t("admin.edit")}
              </h2>
              <button
                type="button"
                onClick={() => setEditing(null)}
                className="font-mono text-[10px] tracking-widest2 uppercase text-cream/70 hover:text-cream"
              >
                {t("admin.cancel")}
              </button>
            </div>
            <div className="grid md:grid-cols-2 gap-x-8 gap-y-6">
              <Field label={t("admin.title_en")} value={form.title_en} onChange={(v) => setForm((f) => ({ ...f, title_en: v }))} required testid="title-en" />
              <Field label={t("admin.title_es")} value={form.title_es} onChange={(v) => setForm((f) => ({ ...f, title_es: v }))} required testid="title-es" />
              <Field label={t("admin.category")} value={form.category} onChange={(v) => setForm((f) => ({ ...f, category: v }))} required testid="category" />
              <Field label={t("admin.cover")} value={form.cover_image} onChange={(v) => setForm((f) => ({ ...f, cover_image: v }))} testid="cover" />
              <Area label={t("admin.excerpt_en")} value={form.excerpt_en} onChange={(v) => setForm((f) => ({ ...f, excerpt_en: v }))} testid="excerpt-en" />
              <Area label={t("admin.excerpt_es")} value={form.excerpt_es} onChange={(v) => setForm((f) => ({ ...f, excerpt_es: v }))} testid="excerpt-es" />
              <Area label={t("admin.body_en")} value={form.body_en} onChange={(v) => setForm((f) => ({ ...f, body_en: v }))} rows={6} testid="body-en" />
              <Area label={t("admin.body_es")} value={form.body_es} onChange={(v) => setForm((f) => ({ ...f, body_es: v }))} rows={6} testid="body-es" />
              <label className="md:col-span-2 flex items-center gap-3 font-mono text-[10px] tracking-widest2 uppercase text-cream/70 cursor-pointer">
                <input
                  type="checkbox"
                  checked={form.published}
                  onChange={(e) => setForm((f) => ({ ...f, published: e.target.checked }))}
                  className="accent-[#B08D57]"
                  data-testid="published"
                />
                {t("admin.published")}
              </label>
            </div>
            <div className="mt-10 flex justify-end gap-4">
              <button type="button" onClick={() => setEditing(null)} className="btn-luxe">
                {t("admin.cancel")}
              </button>
              <button type="submit" className="btn-luxe-solid" data-testid="archive-save">
                {t("admin.save")}
                <span className="arrow-tick" />
              </button>
            </div>
          </form>
        </div>
      ) : null}
    </div>
  );
}

function Field({ label, value, onChange, required, testid }) {
  return (
    <div>
      <label className="font-mono text-[10px] tracking-widest2 uppercase text-cream/55 block mb-2">{label}</label>
      <input
        required={required}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="input-line"
        data-testid={`archive-input-${testid}`}
      />
    </div>
  );
}

function Area({ label, value, onChange, rows = 3, testid }) {
  return (
    <div className="md:col-span-2">
      <label className="font-mono text-[10px] tracking-widest2 uppercase text-cream/55 block mb-2">{label}</label>
      <textarea
        rows={rows}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="input-line resize-none"
        data-testid={`archive-input-${testid}`}
      />
    </div>
  );
}
